import Controller from '@ember/controller';
import { inject as service } from '@ember/service';
import { computed } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import config from 'cio/config/environment';
import LogModel from 'cio/models/log';
import { timeout } from 'ember-concurrency';
import { restartableTask, dropTask } from 'ember-concurrency';
import moment from 'moment-timezone';
import AuthService from 'cio/services/auth';
import FeaturesService from 'ember-feature-flags/services/features';
import AnalyticsService from 'cio/services/analytics';
import DS from 'ember-data';

export default class LogsController extends Controller {
  @service auth!: AuthService;
  @service analytics!: AnalyticsService;
  @service features!: FeaturesService;
  @service store!: DS.Store;

  declare model: any;

  // @ts-ignore queryParams in the array format is not typed
  queryParams = [{ typeQP: 'type' }, { nameQP: 'name' }, { selectedQP: 'selected' }];

  // initializing query params to null and then manipulating through string getters/setters
  // to avoid their default values being added to the URL ("?type=&name=&selected=")
  @tracked typeQP: string | null = null;
  @tracked nameQP: string | null = null;
  @tracked selectedQP: string | null = null;

  get type() {
    return this.typeQP ?? '';
  }
  set type(val: string) {
    this.typeQP = val === '' ? null : val;
  }
  get name() {
    return this.nameQP ?? '';
  }
  set name(val: string) {
    this.nameQP = val === '' ? null : val;
  }
  get selected() {
    return this.selectedQP ?? '';
  }
  set selected(val: string) {
    this.selectedQP = val === '' ? null : val;
  }

  get objectTypes() {
    return this.store.peekAll('object-type').toArray();
  }

  @tracked logs: LogModel[] = [];
  @tracked continuation = null;
  @tracked lastUpdated: number | null = null;

  @computed('model.tab')
  get isShowingAnonymous() {
    return this.model?.tab === 'anonymous';
  }

  queryLogs(query: Record<string, unknown>) {
    return this.store.query('log', query);
  }

  unloadLogs() {
    this.store.unloadAll('log');
  }

  fetchLogs = restartableTask(async () => {
    this.continuation = null;
    await timeout(config.APP.debounceDuration);

    // Anonymous logs and identified logs can share IDs, so when moving between
    // tabs, or loading new logs, we should unload previous logs from the
    // store. Otherwise, ember data will store results between requests and
    // merge two records based on ID, resulting in the wrong UI.
    this.unloadLogs();

    let response = await this.queryLogs({
      type: this.type,
      name: this.name,
      anonymous: this.isShowingAnonymous,
    });

    this.logs = response.toArray();
    this.continuation = response.get('meta.continuation' as any);
    this.lastUpdated = moment().unix();
  });

  fetchMore = dropTask(async () => {
    this.analytics.track('logs', 'more_logs_loaded');

    let response = await this.queryLogs({
      type: this.type,
      name: this.name,
      continuation: this.continuation,
      anonymous: this.isShowingAnonymous,
    });

    this.logs.pushObjects(response.toArray());
    this.continuation = response.get('meta.continuation' as any);
    this.lastUpdated = moment().unix();
  });
}

// DO NOT DELETE: this is how TypeScript knows how to look up your controllers.
declare module '@ember/controller' {
  interface Registry {
    'logs/list': LogsController;
  }
}
